import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest, ApiError } from "../../api/client";
import type { Conference } from "../../api/types";

function toDateInput(iso: string) {
  return iso ? iso.slice(0, 10) : "";
}

export default function ConferencesPage() {
  const [conferences, setConferences] = useState<Conference[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", venue: "", startDate: "", endDate: "", description: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    setConferences(await apiRequest<Conference[]>("/conferences"));
  }

  useEffect(() => {
    void load();
  }, []);

  async function createConference() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest("/conferences", {
        method: "POST",
        body: {
          name: form.name,
          venue: form.venue || null,
          description: form.description || null,
          startDate: form.startDate,
          endDate: form.endDate,
          status: "PUBLISHED",
        },
      });
      setShowForm(false);
      setForm({ name: "", venue: "", startDate: "", endDate: "", description: "" });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create conference");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="toolbar">
        <h1 style={{ margin: 0, flex: 1 }}>Conferences</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + New conference
        </button>
      </div>

      {conferences === null && <p>Loading…</p>}
      {conferences && conferences.length === 0 && <p>No conferences yet. Create the first one to get started.</p>}
      {conferences && conferences.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Venue</th>
              <th>Dates</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {conferences.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.venue ?? "—"}</td>
                <td>
                  {toDateInput(c.startDate)} → {toDateInput(c.endDate)}
                </td>
                <td>{c.status}</td>
                <td>
                  <Link className="btn-ghost" to={`/operator/conferences/${c.id}`}>
                    Sessions →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New conference</h2>
            <div className="stack">
              <div className="field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>
              <div className="field">
                <label>Venue</label>
                <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Start date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>End date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="field">
                <label>Description (optional)</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={busy || !form.name || !form.startDate || !form.endDate}
                onClick={createConference}
              >
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
